import React, { useState } from 'react';

const BADGE_TONES = {
  danger:  { bg: 'var(--danger-bg)',  color: 'var(--danger-text)' },
  warning: { bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
  success: { bg: 'var(--success-bg)', color: 'var(--success-text)' },
  info:    { bg: 'var(--info-bg)',    color: 'var(--info-text)' },
  neutral: { bg: 'var(--surface-light)', color: 'var(--text-secondary)' },
};

// Generic expand/collapse tree, shared by the Sampling Plan editor and the CV Report view
// so both walk a Train -> Equipment -> Location -> Sample Type hierarchy through one
// implementation instead of each page hand-rolling its own expand/collapse state.
//
// Node shape: { id, label, badge?, badgeTone?, children?: Node[] }
const ExpandableTree = ({ nodes, selectedId, onSelect, defaultExpandedIds = [], renderExtra }) => {
  const [expanded, setExpanded] = useState(() => new Set(defaultExpandedIds));

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const renderNode = (node, depth) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSelected = selectedId === node.id;
    const tone = BADGE_TONES[node.badgeTone] || BADGE_TONES.neutral;

    return (
      <div key={node.id}>
        <div
          role="treeitem"
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={isSelected}
          onClick={() => { if (hasChildren) toggle(node.id); if (onSelect) onSelect(node); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
            padding: '6px 8px', paddingLeft: `${8 + depth * 16}px`, borderRadius: '6px',
            background: isSelected ? 'var(--primary-light, rgba(37,99,235,0.08))' : 'transparent',
            fontWeight: depth === 0 ? 700 : (isSelected ? 700 : 500),
            fontSize: depth === 0 ? 'var(--fs-base)' : 'var(--fs-md)'
          }}
        >
          <span style={{ width: '14px', display: 'inline-block', textAlign: 'center', color: 'var(--text-secondary)' }}>
            {hasChildren ? (isExpanded ? '▾' : '▸') : ''}
          </span>
          <span style={{ flex: 1 }}>{node.label}</span>
          {node.badge && (
            <span style={{
              fontSize: 'var(--fs-2xs)', fontWeight: 700, padding: '1px 8px', borderRadius: '999px',
              background: tone.bg, color: tone.color, whiteSpace: 'nowrap'
            }}>
              {node.badge}
            </span>
          )}
          {renderExtra ? renderExtra(node) : null}
        </div>
        {hasChildren && isExpanded && (
          <div>{node.children.map((child) => renderNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  if (!nodes || nodes.length === 0) {
    return <div style={{ padding: '12px', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Nothing to show</div>;
  }

  return <div role="tree">{nodes.map((node) => renderNode(node, 0))}</div>;
};

export default ExpandableTree;
